// ** react imports
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// ** react router imports
import { useNavigate } from 'react-router-dom';

// ** react redux imports
import { useDispatch } from 'react-redux';
import { setVersionData } from '../../../redux/slices/versions';

// ** utils imports
import { formatUtcToLocal } from '../../../utils/Utils';

// ** components imports
import Badge from '../../../components/common/Badge';
import NormalButton from '../../../components/common/Buttons/NormalButton';

// ** enums imports
import {
  ANNOUNCEMENT_CATEGORY,
  ANNOUNCEMENT_URGENCY,
  BADGE_SIZES,
  BADGE_THEMES,
} from '../../../enums/common';

const AnnouncementTableItem = props => {
  // props
  const { data, isSelected, viewMode, onOpenDeleteModal } = props;

  // states
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // refs
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // hooks
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // handlers
  const handleNavigate = () => {
    dispatch(setVersionData(data));
    navigate(`/announcements/update/${data._id}`);
  };

  const getAnnouncementUrgencyStatusBadge = urgency => {
    const themeMap = {
      [ANNOUNCEMENT_URGENCY.LOW]: BADGE_THEMES.PRIMARY,
      [ANNOUNCEMENT_URGENCY.MEDIUM]: BADGE_THEMES.INFO,
      [ANNOUNCEMENT_URGENCY.HIGH]: BADGE_THEMES.WARNING,
      [ANNOUNCEMENT_URGENCY.CRITICAL]: BADGE_THEMES.DANGER,
    };

    const theme = themeMap[urgency] || BADGE_THEMES.DEFAULT;
    return <Badge theme={theme} text={urgency || 'urgency'} size="sm" />;
  };

  const getAnnouncementCategoryStatusBadge = category => {
    const themeMap = {
      [ANNOUNCEMENT_CATEGORY.INFO]: BADGE_THEMES.INFO,
      [ANNOUNCEMENT_CATEGORY.WARNING]: BADGE_THEMES.WARNING,
      [ANNOUNCEMENT_CATEGORY.ERROR]: BADGE_THEMES.DANGER,
      [ANNOUNCEMENT_CATEGORY.SUCCESS]: BADGE_THEMES.SUCCESS,
      [ANNOUNCEMENT_CATEGORY.MAINTENANCE]: BADGE_THEMES.PRIMARY,
    };

    const theme = themeMap[category] || BADGE_THEMES.DEFAULT;
    return <Badge theme={theme} text={category || 'category'} size="sm" />;
  };

  // Render for table view
  if (viewMode === 'table') {
    return (
      <tr
        className={`${
          isSelected ? 'bg-violet-50 dark:bg-violet-900/20' : ''
        } hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors`}
      >
        {/* Title */}
        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
          <div className="text-left font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 hover:underline transition-colors">
            {data?.title || 'N/A'}
          </div>
        </td>

        {/* Category */}
        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
          <div className="text-left text-gray-800 dark:text-gray-100">
            {getAnnouncementCategoryStatusBadge(data?.category)}
          </div>
        </td>

        {/* Urgency Level */}
        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
          <div className="text-left">
            {getAnnouncementUrgencyStatusBadge(data?.urgency)}
          </div>
        </td>

        {/* Is Active */}
        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
          <div className="text-left">
            {data?.isActive ? (
              <Badge
                theme={BADGE_THEMES.ACTIVE}
                text="Yes"
                size={BADGE_SIZES.SM}
              />
            ) : (
              (
                <Badge
                  theme={BADGE_THEMES.INACTIVE}
                  text="No"
                  size={BADGE_SIZES.SM}
                />
              ) || 'N/A'
            )}
          </div>
        </td>

        {/* Created Date */}
        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
          <div className="text-gray-80 dark:text-gray-100">
            {data?.createdAt ? formatUtcToLocal(data?.createdAt) : 'N/A'}
          </div>
        </td>

        {/* Expiry Date */}
        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
          <div className="text-gray-800 dark:text-gray-100">
            {data?.expiryDate ? formatUtcToLocal(data?.expiryDate) : 'N/A'}
          </div>
        </td>

        {/* Actions */}
        <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-center">
          <div className="flex justify-center space-x-2">
            <NormalButton
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              aria-label="Edit"
              onClick={handleNavigate}
            >
              <svg
                className="w-4 h-4 text-gray-600 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </NormalButton>
            <NormalButton
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              aria-label="Delete"
              onClick={() => onOpenDeleteModal(data)}
            >
              <svg
                className="w-4 h-4 text-gray-600 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </NormalButton>
          </div>
        </td>
      </tr>
    );
  }
};

AnnouncementTableItem.propTypes = {
  data: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  viewMode: PropTypes.oneOf(['table', 'card']),
  onOpenDeleteModal: PropTypes.func,
};

export default AnnouncementTableItem;
